import type { ComponentProps, ReactElement } from 'react'
import { forwardRef, useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import _SearchTileBase from './_SearchTileBase'
import _SearchTileContent from './_SearchTileContent'
import _SearchTileContentPrimary from './_SearchTileContentPrimary'
import _SearchTileContentSecondary from './_SearchTileContentSecondary'
import _SearchTileHero from './_SearchTileHero'
import _SearchTilePricing from './_SearchTilePricing'
import type { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'

type Inherited_SearchTileBaseProps = Pick<ComponentProps<typeof _SearchTileBase>, 'state' | 'background' | 'banner' | 'density' | 'treatment'>
type InheritedSearchTileContentProps = Pick<ComponentProps<typeof _SearchTileContent>, 'bottomExtension' | 'topExtension' | 'density'>
type InheritedSearchTileContentPrimaryProps = Pick<ComponentProps<typeof _SearchTileContentPrimary>, 'cancellationPolicyLabel' | 'features' | 'labels' | 'logo' | 'meta' | 'overline' | 'rating' | 'subtitle' | 'title' | 'comparisonCheckbox'>
type InheritedSearchTileHeroProps = Pick<ComponentProps<typeof _SearchTileHero>, 'bookmarkAction' | 'eagerLoadFirstImage' | 'images' | 'onImageChange' | 'onImageLoad' | 'productLabel'>
type InheritedSearchTilePricingProps = Pick<ComponentProps<typeof _SearchTilePricing>, 'action'>

export interface SearchTileLogo {
  full: App.Image
  compact: App.Image
}

interface Props extends Inherited_SearchTileBaseProps, InheritedSearchTileContentProps, InheritedSearchTileContentPrimaryProps, InheritedSearchTileHeroProps, InheritedSearchTilePricingProps, Utils.PropsWithDataAttributes {
  logo?: SearchTileLogo
  /**
   * Price point rows and stacks
   */
  pricePoint?: ReactElement | false
  className?: string
  offer: App.AnyOffer
}

const SearchTile = forwardRef<HTMLDivElement, Props>(function SearchTile(
  {
    state,
    background,
    banner,
    density = 'default',
    treatment = 'default',
    bookmarkAction,
    eagerLoadFirstImage,
    cancellationPolicyLabel,
    features,
    images,
    labels,
    logo,
    meta,
    overline,
    productLabel,
    pricePoint,
    action,
    rating,
    title,
    className,
    subtitle,
    bottomExtension,
    topExtension,
    comparisonCheckbox,
    onImageChange,
    onImageLoad,
    offer,
    ...dataAttributes
  },
  ref,
) {
  const { type: offerType, productType } = offer
  const listId = useMemo(() => uuidv4(), [])

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    onImageChange?.(index, image)
    if (offer && image) {
      trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'search-tile',
          listId,
          offer,
          undefined,
        ),
      )
    }
  }, [onImageChange, offer, listId])

  return <_SearchTileBase
    {...dataAttributes}
    data-product={productType}
    data-offer={offerType}
    state={state}
    background={background}
    banner={banner}
    density={density}
    treatment={treatment}
    className={className}
    ref={ref}
  >
    <_SearchTileHero
      bookmarkAction={bookmarkAction}
      eagerLoadFirstImage={eagerLoadFirstImage}
      images={images}
      productLabel={productLabel}
      onImageChange={handleImageChange}
      onImageLoad={onImageLoad}
    />
    <_SearchTileContent bottomExtension={bottomExtension} topExtension={topExtension} density={density}>
      <_SearchTileContentPrimary
        cancellationPolicyLabel={cancellationPolicyLabel}
        density={density}
        features={features}
        labels={labels}
        meta={meta}
        overline={overline}
        rating={rating}
        title={title}
        subtitle={subtitle}
        logo={logo}
        comparisonCheckbox={comparisonCheckbox}
      />
      <_SearchTileContentSecondary density={density} rating={rating} logo={logo}>
        <_SearchTilePricing action={action} density={density}>
          {pricePoint}
        </_SearchTilePricing>
      </_SearchTileContentSecondary>
    </_SearchTileContent>
  </_SearchTileBase>
})
export default SearchTile
